/**
 * BrokerageSettingsPage - Settings page for managing brokerage connection.
 *
 * Features:
 * - View connected account info
 * - Sync holdings manually
 * - Reconnect with new credentials
 * - Disconnect with confirmation
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { css } from '@emotion/react';
import { Spinner } from '@chakra-ui/react';
import { toast } from 'react-toastify';
import Page from '../Page';
import Modal from '../ModalPopUp/Modal';
import Button from '../Button';
import { colors, fontStyles, spacing } from '../../styles';
import * as stores from '../../stores';
import { getPlaidCredentials, disconnectPlaidAndDeletePortfolio } from '../../storage';
import { usePlaidHoldings } from '../../hooks/usePlaidHoldings';
import { usePlaidFeatureFlag } from '../../hooks/useRemoteConfig';
import arrowBack from '../../assets/Icons/arrowback.svg';
import walletIcon from '../../assets/Icons/wallet.svg';
import PlaidLinkModal from './PlaidLinkModal';

const BrokerageSettingsPage: React.FC = () => {
  const history = useHistory();
  const isPlaidEnabled = usePlaidFeatureFlag();
  const [isConnected, setIsConnected] = useState(false);
  const [institutionName, setInstitutionName] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isDisconnectModalOpen, setIsDisconnectModalOpen] = useState(false);
  const [isPlaidModalOpen, setIsPlaidModalOpen] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);

  const isPurchaseInfoLoaded = stores.Purchase.useState((s) => s.isPurchaseInfoLoaded);
  const purchaserInfo = stores.Purchase.useState((s) => s.purchaserInfo);
  const isSubscribed = purchaserInfo?.entitlements?.active[stores.entitlementIdentifier]?.isActive;

  const { refetch, cacheStatus } = usePlaidHoldings();

  // Helper to check subscription before opening Plaid modal
  const handleOpenPlaidModal = () => {
    // Block action until subscription info is loaded to prevent paywall bypass
    if (!isPurchaseInfoLoaded) {
      return;
    }
    if (!isSubscribed) {
      stores.showPaymentModalByPreference('plaid_connect_brokerage');
      return;
    }
    setIsPlaidModalOpen(true);
  };

  // Redirect to settings if Plaid feature is disabled
  useEffect(() => {
    if (!isPlaidEnabled) {
      history.replace('/settings');
    }
  }, [isPlaidEnabled, history]);

  const checkConnection = useCallback(async () => {
    setIsLoading(true);
    const credentials = await getPlaidCredentials();
    // Check using itemId (accessToken is stored server-side only)
    setIsConnected(!!credentials.itemId);
    setInstitutionName(credentials.institutionName);
    setIsLoading(false);
  }, []);

  useEffect(() => {
    checkConnection();
  }, [checkConnection]);

  const handleSync = async () => {
    setIsSyncing(true);
    try {
      await refetch();
      toast.success('Holdings synced successfully');
    } catch {
      toast.error('Failed to sync holdings');
    } finally {
      setIsSyncing(false);
    }
  };

  const handleDisconnect = async () => {
    await disconnectPlaidAndDeletePortfolio();
    setIsDisconnectModalOpen(false);
    setIsConnected(false);
    setInstitutionName(null);
    toast.success('Brokerage disconnected');
  };

  const handlePlaidSuccess = async () => {
    // PlaidLinkModal shows "Syncing your holdings..." while this runs
    await refetch();
    await checkConnection();
    toast.success('Brokerage connected successfully');
    // Modal will be closed by PlaidLinkModal after this function completes
    setIsPlaidModalOpen(false);
  };

  const styles = {
    darkmodeStyle: css`
      html[data-theme='dark'] & {
        background-color: ${colors.liquorice};
        color: ${colors.sugar};
      }
    `,
  };

  if (isLoading) {
    return (
      <Page>
        <div
          css={css`
            display: flex;
            justify-content: center;
            align-items: center;
            height: 200px;
          `}
        >
          <Spinner size="lg" color={colors.petrolBlue} />
        </div>
      </Page>
    );
  }

  return (
    <Page>
      {/* Header */}
      <div
        css={css`
          display: flex;
          align-items: center;
          gap: ${spacing.centi};
          margin-bottom: ${spacing.deci};
        `}
      >
        <button
          onClick={() => history.goBack()}
          css={css`
            background: none;
            border: none;
            padding: 0;
            cursor: pointer;
            display: flex;
            align-items: center;
          `}
        >
          <img src={arrowBack} alt="Back" />
        </button>
        <h1
          css={css`
            color: ${colors.liquorice};
            ${fontStyles.shark};
            ${styles.darkmodeStyle}
            margin: 0;
          `}
        >
          Brokerage
        </h1>
      </div>

      {isConnected ? (
        <>
          {/* Connected Account Section */}
          <h2
            css={css`
              color: ${colors.liquorice};
              margin: ${spacing.deci} 0 ${spacing.centi} 0;
              ${fontStyles.lion};
              ${styles.darkmodeStyle}
            `}
          >
            Connected Account
          </h2>

          <div
            css={css`
              background: ${colors.snow};
              border-radius: 12px;
              padding: ${spacing.deci};
              margin-bottom: ${spacing.deci};
              html[data-theme='dark'] & {
                background: ${colors.charcoal};
              }
            `}
          >
            <div
              css={css`
                display: flex;
                align-items: center;
                gap: ${spacing.centi};
              `}
            >
              <img src={walletIcon} alt="" width="24" height="24" />
              <div>
                <div
                  css={css`
                    ${fontStyles.cat};
                    font-weight: 600;
                    color: ${colors.liquorice};
                    html[data-theme='dark'] & {
                      color: ${colors.sugar};
                    }
                  `}
                >
                  {institutionName || 'Connected Brokerage'}
                </div>
                {/* Cache status */}
                {cacheStatus.cacheAgeMinutes !== null && (
                  <div
                    css={css`
                      ${fontStyles.flea};
                      color: ${cacheStatus.isStale ? colors.tangerine : colors.silver};
                      margin-top: 2px;
                    `}
                  >
                    {cacheStatus.isStale
                      ? 'Data may be outdated'
                      : cacheStatus.cacheAgeMinutes === 0
                        ? 'Just updated'
                        : cacheStatus.cacheAgeMinutes < 60
                          ? `Updated ${cacheStatus.cacheAgeMinutes} min ago`
                          : `Updated ${Math.floor(cacheStatus.cacheAgeMinutes / 60)}h ago`}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <Button block variant="secondary" onClick={handleSync} disabled={isSyncing || !cacheStatus.canRefresh}>
            {isSyncing ? <Spinner size="sm" /> : 'Sync Now'}
          </Button>
          {cacheStatus.refreshesRemaining !== undefined && (
            <div
              css={css`
                ${fontStyles.flea};
                color: ${cacheStatus.canRefresh ? colors.silver : colors.tangerine};
                text-align: center;
                margin-top: ${spacing.milli};
              `}
            >
              {cacheStatus.canRefresh
                ? `${cacheStatus.refreshesRemaining} refreshes remaining today`
                : 'Daily refresh limit reached'}
            </div>
          )}

          <div
            css={css`
              margin-top: ${spacing.centi};
            `}
          >
            <Button block variant="secondary" onClick={handleOpenPlaidModal}>
              Reconnect
            </Button>
          </div>

          {/* Disconnect Section */}
          <div
            css={css`
              margin-top: ${spacing.regular};
              padding-top: ${spacing.deci};
              border-top: 1px solid ${colors.cloud};
              html[data-theme='dark'] & {
                border-color: ${colors.charcoal};
              }
            `}
          >
            <Button
              block
              variant="secondary"
              onClick={() => setIsDisconnectModalOpen(true)}
              css={css`
                color: ${colors.cherry} !important;
                border-color: ${colors.cherry} !important;
              `}
            >
              Disconnect
            </Button>
          </div>
        </>
      ) : (
        /* Not Connected State */
        <div
          css={css`
            display: flex;
            flex-direction: column;
            align-items: center;
            text-align: center;
            padding: ${spacing.regular} 0;
          `}
        >
          <img
            src={walletIcon}
            alt=""
            width="48"
            height="48"
            css={css`
              margin-bottom: ${spacing.deci};
              opacity: 0.6;
            `}
          />
          <h2
            css={css`
              ${fontStyles.dog};
              color: ${colors.liquorice};
              margin: 0 0 ${spacing.centi} 0;
              html[data-theme='dark'] & {
                color: ${colors.sugar};
              }
            `}
          >
            Connect Your Brokerage
          </h2>
          <p
            css={css`
              ${fontStyles.mouse};
              color: ${colors.silver};
              margin: 0 0 ${spacing.deci} 0;
            `}
          >
            Import your holdings automatically from Fidelity, Schwab, Robinhood, and 10,000+ institutions.
          </p>
          <Button block onClick={handleOpenPlaidModal}>
            Connect with Plaid
          </Button>
          <div
            css={css`
              ${fontStyles.flea};
              color: ${colors.silver};
              margin-top: ${spacing.centi};
              display: flex;
              align-items: center;
              gap: ${spacing.milli};
            `}
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
              <path
                d="M12 2C9.23858 2 7 4.23858 7 7V10H6C4.89543 10 4 10.8954 4 12V20C4 21.1046 4.89543 22 6 22H18C19.1046 22 20 21.1046 20 20V12C20 10.8954 19.1046 10 18 10H17V7C17 4.23858 14.7614 2 12 2ZM9 7C9 5.34315 10.3431 4 12 4C13.6569 4 15 5.34315 15 7V10H9V7Z"
                fill={colors.silver}
              />
            </svg>
            Your credentials are never stored by Bloom
          </div>
        </div>
      )}

      {/* Plaid Link Modal */}
      <PlaidLinkModal
        isOpen={isPlaidModalOpen}
        onClose={() => setIsPlaidModalOpen(false)}
        onSuccess={handlePlaidSuccess}
      />

      {/* Syncing Modal */}
      <Modal isOpen={isSyncing} onClose={() => undefined} title="" maxWidth="400px" isDismissable={false}>
        <div
          css={css`
            padding: ${spacing.regular};
            display: flex;
            flex-direction: column;
            align-items: center;
            text-align: center;
            gap: ${spacing.deci};
          `}
        >
          <Spinner size="lg" color={colors.petrolBlue} thickness="3px" />
          <div
            css={css`
              ${fontStyles.dog};
              color: var(--text-color);
              margin-top: ${spacing.centi};
            `}
          >
            Syncing your holdings...
          </div>
          <div
            css={css`
              ${fontStyles.mouse};
              color: ${colors.silver};
            `}
          >
            This may take a few seconds
          </div>
        </div>
      </Modal>

      {/* Disconnect Confirmation Modal */}
      <Modal
        isOpen={isDisconnectModalOpen}
        onClose={() => setIsDisconnectModalOpen(false)}
        title="Disconnect Brokerage?"
        maxWidth="400px"
      >
        <div
          css={css`
            padding: ${spacing.deci};
          `}
        >
          <p
            css={css`
              ${fontStyles.cat};
              color: ${colors.tungsten};
              margin: 0 0 ${spacing.deci} 0;
              html[data-theme='dark'] & {
                color: ${colors.silver};
              }
            `}
          >
            This will remove the connection to your brokerage account and delete your Plaid portfolio.
          </p>
          <p
            css={css`
              ${fontStyles.mouse};
              color: ${colors.silver};
              margin: 0 0 ${spacing.deci} 0;
            `}
          >
            You can reconnect anytime.
          </p>
          <div
            css={css`
              display: flex;
              flex-direction: column;
              gap: ${spacing.centi};
            `}
          >
            <Button block variant="secondary" onClick={() => setIsDisconnectModalOpen(false)}>
              Cancel
            </Button>
            <Button
              block
              variant="secondary"
              onClick={handleDisconnect}
              css={css`
                color: ${colors.cherry} !important;
                border-color: ${colors.cherry} !important;
              `}
            >
              Disconnect
            </Button>
          </div>
        </div>
      </Modal>
    </Page>
  );
};

export default BrokerageSettingsPage;
